/**
 * Small presentational pieces shared by the three coach event screens.
 *
 * These sit on top of components/ui.tsx and reuse its classes and custom
 * properties rather than inventing a second look. Anything used by exactly one
 * screen lives in that screen's file instead.
 */

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Card } from '../../components/ui'
import { pluralize } from '../../lib/format'
import type { CoachServiceReadiness } from '../../types/db'

/* -------------------------------------------------------------------------- */
/* Feedback after a write                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The result of the last action, in place, right where the button was.
 *
 * A refusal is shown word for word: every message thrown by lib/api.ts is
 * written to be read by a coach ("Say what is missing or damaged before you
 * sign this off"), so rewording it here would only make it vaguer.
 */
export function ActionFeedback({
  error,
  done,
}: {
  error: string | null
  done: string | null
}) {
  if (!error && !done) return null
  if (error) {
    return (
      <div
        className="notice"
        role="alert"
        style={{
          marginTop: 'var(--sp-3)',
          background: 'var(--danger-soft)',
          color: 'var(--danger-soft-ink)',
          borderColor: 'color-mix(in srgb, var(--danger) 30%, transparent)',
        }}
      >
        <div className="notice__body">{error}</div>
      </div>
    )
  }
  return (
    <div className="notice notice--success" role="status" style={{ marginTop: 'var(--sp-3)' }}>
      <div className="notice__body">{done}</div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* One thing waiting on the coach                                             */
/* -------------------------------------------------------------------------- */

/**
 * A single line on the "waiting on you" list. The whole row is the tap target,
 * which is the only sensible size for a thumb, and the label on the right says
 * what tapping it is for.
 */
export function TaskRow({
  to,
  title,
  detail,
  cta,
  urgent = false,
  icon,
}: {
  to: string
  title: string
  detail: string
  cta: string
  urgent?: boolean
  icon?: ReactNode
}) {
  return (
    <Link className={`rowitem${urgent ? ' rowitem--unread' : ''}`} to={to}>
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      <div className="rowitem__main">
        <p className="rowitem__title" style={{ whiteSpace: 'normal' }}>
          {title}
        </p>
        <p className="rowitem__sub">{detail}</p>
      </div>
      <Badge tone={urgent ? 'warning' : 'info'}>{cta}</Badge>
    </Link>
  )
}

/* -------------------------------------------------------------------------- */
/* The qualification gate, explained                                          */
/* -------------------------------------------------------------------------- */

function readinessRank(row: CoachServiceReadiness): number {
  switch (row.qualification_status) {
    case 'approved':
      return 0
    case 'pending':
      return 1
    case 'revoked':
      return 3
    default:
      return 2
  }
}

/**
 * Why this list looks the way it does.
 *
 * The gating rule is the whole product: a coach only ever sees events for
 * services they are qualified for. A coach who does not understand that reads
 * an empty schedule as a broken app, so the rule is stated, the services they
 * hold are named, and the ones they could unlock are shown with how far along
 * they are.
 */
export function QualificationGate({ readiness }: { readiness: CoachServiceReadiness[] }) {
  const rows = [...readiness].sort(
    (a, b) => readinessRank(a) - readinessRank(b) || a.service_name.localeCompare(b.service_name),
  )
  const qualified = rows.filter((r) => r.qualification_status === 'approved')

  return (
    <Card tone="muted">
      <p className="card__title">Why you see these events</p>
      <p className="small muted" style={{ marginTop: 'var(--sp-2)' }}>
        {qualified.length > 0
          ? `You are qualified for ${qualified.length} ${pluralize(
              qualified.length,
              'service',
            )}, and you only ever see events for those. Everything else stays out of your list until Michel approves you.`
          : 'Events appear here once Michel approves you for a service. Finish the modules that service needs, then ask him for the approval call.'}
      </p>

      <div className="stack stack--tight" style={{ marginTop: 'var(--sp-4)' }}>
        {rows.map((row) => (
          <div key={row.service_id} className="row row--between" style={{ gap: 'var(--sp-3)' }}>
            <div style={{ minWidth: 0 }}>
              <p className="small" style={{ fontWeight: 550 }}>
                {row.service_name}
              </p>
              {row.qualification_status !== 'approved' ? (
                <p className="faint">
                  {row.modules_completed} of {row.modules_required}{' '}
                  {pluralize(row.modules_required, 'module')} done
                </p>
              ) : null}
            </div>
            <ReadinessBadge row={row} />
          </div>
        ))}
      </div>

      <Link
        className="btn btn--secondary btn--block"
        to="/training"
        style={{ marginTop: 'var(--sp-4)' }}
      >
        Open training
      </Link>
    </Card>
  )
}

function ReadinessBadge({ row }: { row: CoachServiceReadiness }) {
  switch (row.qualification_status) {
    case 'approved':
      return <Badge tone="positive">Qualified</Badge>
    case 'pending':
      return <Badge tone="warning">Waiting for Michel</Badge>
    case 'revoked':
      return <Badge tone="danger">Closed</Badge>
    default:
      return (
        <Badge tone={row.modules_complete ? 'info' : 'neutral'}>
          {row.modules_complete ? 'Ready to ask' : 'Locked'}
        </Badge>
      )
  }
}
